import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Search, Package, ArrowRight, Coins } from "lucide-react";
import { getRecentOrders, getSavedEmail } from "../lib/orderStorage";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_PILL = {
  created: { label: "Pending", cls: "bg-amber-100 text-amber-800" },
  paid: { label: "Paid", cls: "bg-green-100 text-green-800" },
  shipped: { label: "Shipped", cls: "bg-blue-100 text-blue-800" },
  delivered: { label: "Delivered", cls: "bg-green-100 text-green-800" },
  failed: { label: "Failed", cls: "bg-red-100 text-red-800" },
  refunded: { label: "Refunded", cls: "bg-neutral-200 text-neutral-700" },
  refund_initiated: { label: "Refund Initiated", cls: "bg-amber-100 text-amber-800" },
  disputed: { label: "Disputed", cls: "bg-red-100 text-red-800" },
  signature_failed: { label: "Verification Failed", cls: "bg-red-100 text-red-800" },
};

export default function MyOrders() {
  const [email, setEmail] = useState(getSavedEmail());
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [loyalty, setLoyalty] = useState(null);
  const [referral, setReferral] = useState(null);
  const recent = getRecentOrders();

  const lookup = async (e) => {
    if (e) e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    setSearched(true);
    try {
      const [{ data: orderData }, { data: loyaltyData }, { data: referralData }] = await Promise.all([
        axios.get(`${API}/orders`, { params: { email: email.trim() } }),
        axios.get(`${API}/loyalty/${encodeURIComponent(email.trim())}`),
        axios.get(`${API}/referral/${encodeURIComponent(email.trim())}`),
      ]);
      setOrders(orderData);
      setLoyalty(loyaltyData);
      setReferral(referralData);
    } catch {
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (email) lookup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <main className="max-w-[1100px] mx-auto px-4 md:px-8 py-8 md:py-14" data-testid="my-orders-page">
      <h1 className="font-display text-3xl md:text-5xl font-black leading-tight">My Orders</h1>
      <p className="text-neutral-500 mt-1 text-sm md:text-base">Track your purchases and their status.</p>

      <form onSubmit={lookup} className="mt-6 md:mt-8 flex flex-col sm:flex-row gap-2 max-w-xl" data-testid="orders-lookup-form">
        <div className="flex-1 flex items-center gap-2 border border-neutral-300 rounded-full px-5 h-12 focus-within:border-neutral-900">
          <Search className="w-4 h-4 text-neutral-500 flex-shrink-0" />
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type="email"
            placeholder="Enter the email used at checkout..."
            className="flex-1 outline-none text-sm bg-transparent min-w-0"
            data-testid="orders-email-input"
          />
        </div>
        <button className="h-12 px-6 rounded-full bg-[#FF5A1F] hover:bg-neutral-900 text-white font-semibold text-sm transition whitespace-nowrap" data-testid="orders-lookup-btn">
          Find Orders
        </button>
      </form>

      {/* Recent (localStorage) */}
      {!searched && recent.length > 0 && (
        <section className="mt-12">
          <h2 className="font-display text-xl font-black">Recent on this device</h2>
          <div className="mt-4 grid sm:grid-cols-2 gap-4">
            {recent.map((r) => (
              <Link
                key={r.id}
                to={`/order/${r.id}`}
                className="bg-neutral-50 hover:bg-white border border-transparent hover:border-neutral-200 rounded-2xl p-5 transition group flex justify-between items-center"
                data-testid={`recent-order-${r.id}`}
              >
                <div>
                  <div className="text-xs text-neutral-500">{new Date(r.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</div>
                  <div className="mt-1 font-mono text-sm">{r.razorpay_order_id}</div>
                  <div className="mt-2 flex items-center gap-2">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${STATUS_PILL[r.status]?.cls || "bg-neutral-100 text-neutral-700"}`}>
                      {STATUS_PILL[r.status]?.label || r.status}
                    </span>
                    <span className="text-xs text-neutral-500">{r.items_count} item{r.items_count !== 1 ? "s" : ""}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-bold">₹{r.amount_inr}</div>
                  <ArrowRight className="w-4 h-4 mt-1 opacity-0 group-hover:opacity-100 transition" />
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Loyalty balance card */}
      {searched && loyalty && loyalty.balance > 0 && (
        <section className="mt-8 bg-gradient-to-br from-purple-600 to-[#FF5A1F] text-white rounded-3xl p-5 md:p-8" data-testid="loyalty-card">
          <div className="flex items-center gap-3 md:gap-4">
            <div className="w-11 h-11 md:w-14 md:h-14 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
              <Coins className="w-5 h-5 md:w-6 md:h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] md:text-xs uppercase tracking-widest opacity-80">Rewards Balance</div>
              <div className="font-display text-3xl md:text-4xl font-black mt-0.5 md:mt-1">{loyalty.balance} pts</div>
              <div className="text-xs md:text-sm opacity-90 mt-1 leading-snug">
                Worth ₹{loyalty.balance * loyalty.redeem_value_per_point} · Redeem at checkout ({loyalty.min_redeem_points} pts min)
              </div>
            </div>
            <div className="hidden md:block text-right text-xs opacity-80">
              <div>Earned: {loyalty.total_earned}</div>
              <div>Redeemed: {loyalty.total_redeemed}</div>
            </div>
          </div>
        </section>
      )}

      {/* Referral share card */}
      {searched && referral && (
        <section className="mt-4 bg-white border-2 border-neutral-900 rounded-3xl p-5 md:p-6" data-testid="referral-share">
          <div>
            <div className="text-[10px] md:text-xs uppercase tracking-widest text-neutral-500 font-semibold">Refer & Earn</div>
            <div className="font-display text-lg md:text-xl font-black mt-1 leading-tight">Give ₹{referral.bonus_new_customer}, Get {referral.bonus_referrer} pts</div>
            <p className="text-xs md:text-sm text-neutral-600 mt-1 leading-snug">
              Share your code — friend gets {referral.bonus_new_customer} points on their first order, you get {referral.bonus_referrer} points when they pay.
            </p>
          </div>
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <div className="bg-neutral-100 rounded-full px-4 md:px-5 h-11 flex items-center font-mono font-bold text-base md:text-lg flex-1 min-w-0 justify-center">
              {referral.code}
            </div>
            <button
              onClick={() => { navigator.clipboard.writeText(referral.code); toast?.success && toast.success("Code copied"); }}
              className="h-11 px-4 md:px-5 rounded-full bg-neutral-900 hover:bg-[#FF5A1F] text-white text-xs md:text-sm font-bold transition"
              data-testid="copy-ref-btn"
            >Copy</button>
            <a
              href={`https://wa.me/?text=${encodeURIComponent(`Hey! Shop at Stealodeal and use my code ${referral.code} to get ${referral.bonus_new_customer} bonus points on your first order → stealodeal.com`)}`}
              target="_blank" rel="noopener noreferrer"
              className="h-11 px-4 md:px-5 rounded-full bg-green-600 hover:bg-green-700 text-white text-xs md:text-sm font-bold transition flex items-center"
              data-testid="share-whatsapp-btn"
            >WhatsApp</a>
          </div>
          {referral.referrals_count > 0 && (
            <div className="mt-4 text-xs text-neutral-500">🎉 {referral.referrals_count} friend{referral.referrals_count !== 1 ? "s" : ""} used your code — thanks for spreading the word!</div>
          )}
        </section>
      )}

      {/* Server results */}
      {searched && (
        <section className="mt-10">
          {loading ? (
            <div className="text-neutral-500 animate-pulse py-10 text-center">Searching...</div>
          ) : orders.length === 0 ? (
            <div className="py-16 text-center bg-neutral-50 rounded-3xl">
              <Package className="w-10 h-10 text-neutral-400 mx-auto" />
              <p className="mt-3 text-neutral-600">No orders found for that email.</p>
              <Link to="/shop" className="mt-4 inline-block text-[#FF5A1F] font-semibold">Start shopping →</Link>
            </div>
          ) : (
            <div className="space-y-3">
              {orders.map((o) => (
                <Link
                  key={o.id}
                  to={`/order/${o.id}`}
                  className="block bg-white border border-neutral-200 hover:border-neutral-900 rounded-2xl p-5 transition"
                  data-testid={`order-row-${o.id}`}
                >
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div>
                      <div className="text-xs text-neutral-500">{new Date(o.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</div>
                      <div className="mt-1 font-mono text-sm">{o.razorpay_order_id}</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${STATUS_PILL[o.status]?.cls || "bg-neutral-100 text-neutral-700"}`}>
                        {STATUS_PILL[o.status]?.label || o.status}
                      </span>
                      <div className="font-bold text-lg">₹{o.amount_inr}</div>
                    </div>
                  </div>
                  <div className="mt-2 text-xs text-neutral-500 line-clamp-1">
                    {(o.items || []).map((i) => `${i.qty}× ${i.title}`).join(" · ")}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      )}
    </main>
  );
}
